import i18n from '@/libs/i18n'
import store from '@/store'
import { PAYMENT_TYPES } from '@/constants/order-constants'
import getReceiptInfo from '@/utils/print-receipt-info'
import LocalizationService from '@/services/localization-service'

const MAX_LEN = 48
const MAX_LEN_SUMMARY = 46
const DIVIDER = '------------------------------------------------\n'

const countDecimals = value => (((value % 1) !== 0) ? value.toString().split('.')[1].length : 0)
function formatValue(number, precision = 3) {
  return (number || 0)?.toFixed ? (number || 0).toFixed(Math.min(countDecimals(number), precision)) : (number || 0)
}

class EpsonPrinter {
  ePosDev = null

  printer = null

  connected = false

  device = null

  intervalID = null

  constructor(ip, port = 8043) {
    // eslint-disable-next-line new-cap
    this.ePosDev = new window.epson.ePOSDevice()
    this.ePosDev.onreconnecting = this.onReconnecting
    this.ePosDev.onreconnect = this.onReconnect
    this.ePosDev.ondisconnect = this.onDisconnect

    this.ip = ip
    this.port = port
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnecting() {
    console.log('Printer reconnecting')
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect() {
    console.log('Printer reconnected')
  }

  onDisconnect() {
    console.log('Printer disconnected')
    setTimeout(() => {
      console.log(`Reconnect printer after disconnect with ip "${this.ip}" and port "${this.port}"`)
      this.printer.connect(this.ip, this.port, response => this.connectCallback(response), { eposprint: true })
    }, 10000)
  }

  async connect() {
    this.ePosDev.ondisconnect = null
    this.ePosDev.disconnect()
    console.log(`Connect printer with ip "${this.ip}" and port "${this.port}"`)
    this.ePosDev.connect(this.ip, this.port, response => this.connectCallback(response), { eposprint: true })
  }

  connectCallback(data) {
    console.log('Printer connect callback', data)
    clearInterval(this.intervalID)
    this.intervalID = null
    this.ePosDev.ondisconnect = this.onDisconnect

    console.log(`callback ${data}`)
    if (data !== 'OK' && data !== 'SSL_CONNECT_OK') {
      this.connected = false
      return
    }

    this.ePosDev.createDevice(
      'local_printer',
      this.ePosDev.DEVICE_TYPE_PRINTER,
      { crypto: false, buffer: false },
      (printer, returnedCode) => {
        console.log(`created ${returnedCode}`)
        if (returnedCode === 'OK') {
          this.printer = printer
          this.connected = true
        } else {
          this.connected = false
          throw new Error(returnedCode)
        }
      },
    )
  }

  // consoleLogIt(summary) {
  //   const keysToIgnoreFromFormatting = ['business', 'branch', 'user', 'register', 'start-time', 'close-time', 'generated-at']
  //   for (let i = 0; i < summary.length; i += 1) {
  //     const item = summary[i]
  //     if (item && item.label) {
  //       const { key } = item
  //       const { value } = item
  //       const { label } = item

  //       const formattedValue = keysToIgnoreFromFormatting.includes(key) ? value : formatValue(value, 2)

  //       console.log(`${label}:   ${formattedValue}\n`)
  //     } else {
  //       console.log(DIVIDER)
  //     }
  //   }

  //   return true
  // }

  printSummary(summary) {
    // this.consoleLogIt(summary) // for test
    const { printer } = this
    const keysToIgnoreFromFormatting = ['business', 'branch', 'user', 'register', 'start-time', 'close-time', 'generated-at']

    try {
      if (!printer || !this.connected) {
        return false
      }

      printer.addTextSmooth(true)
      printer.addTextFont(printer.FONT_A)
      printer.addTextAlign(printer.ALIGN_LEFT)

      for (let i = 0; i < summary.length; i += 1) {
        const item = summary[i]
        if (item && item.label) {
          const { key } = item
          const { value } = item
          const { label } = item
          const formattedValue = keysToIgnoreFromFormatting.includes(key) ? value : formatValue(value, 2)
          // Print key and value on separate lines
          printer.addText(`${label}:   ${formattedValue}\n`)
        } else {
          printer.addText(DIVIDER)
        }
      }

      printer.addCut(printer.CUT_FEED)
      printer.addPulse(printer.DRAWER_1, printer.PULSE_100)
      printer.send()

      return true
    } catch (e) {
      console.error(e)
      return false
    }
  }

  printOrder(order) {
    const { printer } = this
    try {
      if (!printer || !this.connected) {
        return false
      }

      const user = store.getters['auth/user']
      const cashierName = user?.subuser_name || user?.name

      const info = getReceiptInfo(order)
      const { locale } = i18n
      const precision = info.originalCurrency === 'KWD' ? 3 : 2
      const formatNumber = value => formatValue(value, precision)

      const subtotal_label = i18n.t('payment.subtotal').toString()
      const discount_label = i18n.t('orders.discount').toString()
      const coupon_label = i18n.t('orders.coupon').toString()
      const delivery_label = i18n.t('orders.delivery-fees').toString()
      const taxes_label = i18n.t('orders.taxes').toString()
      const total_label = i18n.t('payment.total').toString()

      const textCanvas = document.getElementById('text-canvas')
      printer.addTextSmooth(true)
      printer.addTextFont(printer.FONT_A)
      printer.addTextAlign(printer.ALIGN_CENTER)

      try {
        if (user?.receipt_logo_context) {
          printer.brightness = 1
          printer.halftone = printer.HALFTONE_ERROR_DIFFUSION
          printer.addImage(user?.receipt_logo_context, 0, 0, 460, 280, printer.COLOR_1, printer.MODE_MONO)
        }
      } catch (e) {
        console.error(e)
      }

      printer.addTextStyle(true, false, false, printer.COLOR_1)
      printer.addText(` ${i18n.t('orders.order-receipt').toString()} \n`)
      printer.addTextStyle(false, false, false, printer.COLOR_1)

      printer.addTextAlign(printer.ALIGN_CENTER)

      if (info.businessName) {
        printer.addText(`${info.businessName || ''}\n`)
      }
      printer.addText(`${i18n.t('orders.order-no').toString()}\n`)
      printer.addText(`#${order?.ids || order?.id}\n`)
      printer.addText(`${i18n.t(info.orderType).toString()}\n`)

      if ((info.isDelivery && info.deliveryTime) || info.scheduledTime) {
        printer.addText(`${info.isDelivery && info.deliveryTime ? info.deliveryTime : info.scheduledTime}\n`)
      }
      if (!info.takeaway) {
        if (order.client_name) {
          printer.addText(order.client_name ? `${order.client_name}\n` : '')
        }
        const phone = order.phone || order.client_phone
        if (phone) {
          printer.addText(phone ? `Tel: ${phone}\n` : '')
        }
        if (cashierName) {
          printer.addText(`${i18n.t('users.roles.cashier')}: ${cashierName}\n`)
        }
      }
      if (order.notes) {
        printer.addText(order.notes ? `${i18n.t('general.note').toString()}: ${order.notes}\n` : '')
      }

      printer.addText(info.orderTypeDetails ? `${info.orderTypeDetails}\n` : '')
      printer.addTextAlign(printer.ALIGN_LEFT)
      printer.addText(DIVIDER)
      printer.addText(`Qty Name${Array(MAX_LEN - 14).fill(' ').join('')}Amount`)
      printer.addText(DIVIDER)
      order.order_lines.forEach(line => {
        const itemNameEn = LocalizationService.getTranslate(line?.item, 'name', 'en', true)
        const itemNameAr = (LocalizationService.getTranslate(line?.item, 'name', 'ar', true) || '')/* split("").reverse().join("") */
        const itemName = locale === 'en' ? itemNameEn : itemNameAr
        const anotherItemName = locale === 'en' ? itemNameAr : itemNameEn
        const qty = `${formatNumber(line.quantity * (line.price_no_discount || line.price))} ${info.originalCurrency}`

        if (itemName && anotherItemName) {
          const spaces = Math.max(MAX_LEN - (`${`${line.quantity}`.padEnd(3, ' ')} ${itemNameEn}${qty}`).length, 0)
          printer.addText(`${`${line.quantity}`.padEnd(3, ' ')} ${itemNameEn}${Array(spaces).fill(' ').join('')}${qty}\n`)

          try {
            const ctx = textCanvas.getContext('2d')
            ctx.font = '22px "Times New Roman", serif'
            ctx.clearRect(0, 0, textCanvas.width, textCanvas.height)
            ctx.fillText(itemNameAr, 45, 22)
            printer.addImage(ctx, 0, 0, 378, 30, printer.COLOR_1, printer.MODE_MONO)
          } catch (e) {
            console.error(e)
            printer.addText(`    ${itemNameAr}\n`)
          }
        } else {
          const name = `${`${line.quantity}`.padEnd(3, ' ')} ${itemName || line.item.name}`
          const spaces = Math.max(MAX_LEN - (name + qty).length, 0)
          printer.addText(`${name}${Array(spaces).fill(' ').join('')}${qty}\n`)
        }
        const modifiers = line.modifiers ?? []

        modifiers.forEach(modifier => {
          const modifierNameEn = LocalizationService.getTranslate(modifier, null, 'en', true)
          const modifierNameAr = (LocalizationService.getTranslate(modifier, null, 'ar', true) || '').split('').reverse().join('')
          let modifierName = locale === 'en' ? modifierNameEn : modifierNameAr
          const anotherModifierName = locale === 'en' ? modifierNameAr : modifierNameEn
          if (modifierName && anotherModifierName) {
            printer.addTextAlign(printer.ALIGN_LEFT)
            printer.addText(`${modifierName}\n`)
            printer.addTextAlign(printer.ALIGN_CENTER)
            modifierName = anotherModifierName
          }
          if (!modifierName) {
            modifierName = LocalizationService.getTranslate(modifier)
          }
          const modifierQty = `${formatNumber(line.quantity * modifier.price)} ${info.originalCurrency}`
          const modifierSpaces = Math.max(MAX_LEN - (modifierName + modifierQty).length, 0)
          printer.addText(`${modifierName}${Array(modifierSpaces).fill(' ').join('')}${modifierQty}\n`)
        })

        if (line.comment) {
          printer.addText(`${i18n.t('general.note')} ${line.comment}\n`)
        }

        printer.addText(DIVIDER)
      })

      const subtotal_value = `${formatNumber(order.subtotal || order.amount_no_coupon)} ${info.originalCurrency}`
      printer.addText(`${subtotal_label}:${Array(Math.max(MAX_LEN_SUMMARY - (subtotal_label + subtotal_value).length, 0)).fill(' ').join('')}${subtotal_value}\n`)
      if (info.discountAmount) {
        const discount_text = `${formatNumber(info.discountAmount)} ${info.originalCurrency}`
        printer.addText(`${discount_label}:${Array(Math.max(MAX_LEN_SUMMARY - (discount_label + discount_text).length, 0)).fill(' ').join('')}${discount_text}\n`)
      }
      if (order.coupon) {
        if (order.coupon.discount_type === 'fixed') {
          const coupon_value = `${formatNumber(order.coupon.discount_value)} ${info.originalCurrency}`
          printer.addText(`${coupon_label}:${Array(Math.max(MAX_LEN_SUMMARY - (coupon_label + coupon_value).length, 0)).fill(' ').join('')}${coupon_value}\n`)
        } else {
          const coupon_value = `${order.coupon.discount_value}%`
          printer.addText(`${coupon_label}:${Array(Math.max(MAX_LEN_SUMMARY - (coupon_label + coupon_value).length, 0)).fill(' ').join('')}${coupon_value}\n`)
        }
      }
      if (info.isDelivery && order.fees) {
        const fees_text = `${formatNumber(order.fees)} ${info.originalCurrency}`
        printer.addText(`${delivery_label}:${Array(Math.max(MAX_LEN_SUMMARY - (delivery_label + fees_text).length, 0)).fill(' ').join('')}${fees_text}\n`)
      }
      if (order.taxes_amount) {
        const taxes_text = `${formatNumber(order.taxes_amount)} ${info.originalCurrency}`
        printer.addText(`${taxes_label}:${Array(Math.max(MAX_LEN_SUMMARY - (taxes_label + taxes_text).length, 0)).fill(' ').join('')}${taxes_text}\n`)
      }
      const total_text = `${formatNumber(order.original_amount || order.amount)} ${info.originalCurrency}`
      printer.addText(`${total_label}:${Array(Math.max(MAX_LEN_SUMMARY - (total_label + total_text).length, 0)).fill(' ').join('')}${total_text}\n`)
      printer.addText(DIVIDER)
      printer.addTextAlign(printer.ALIGN_CENTER)

      const isPaid = order.payment_status_key ?? order.payment_status
      if (isPaid) {
        printer.addText(`${order.payment_type_key === PAYMENT_TYPES.CASH ? i18n.t('payment.cash') : i18n.t('payment.credit-card')}\n`)
      }
      printer.addText(isPaid ? `${i18n.t('subscription.paid').toString().toUpperCase()}\n` : `${i18n.t('general.unpaid').toString().toUpperCase()}\n`)

      if (isPaid && order.payment_type_key === PAYMENT_TYPES.ONLINE_PAYMENT && order.payment_id) {
        printer.addText(DIVIDER)
        printer.addText(`Transaction Id: ${order.payment_id}\n`)
      }

      if (isPaid && order.payment_type_key === PAYMENT_TYPES.CASH && order.cash_amount) {
        printer.addText(DIVIDER)

        let value = `${formatNumber(order.cash_amount)} ${info.originalCurrency}`
        printer.addText(`${i18n.t('payment.cash')}:${Array(Math.max(MAX_LEN_SUMMARY - (i18n.t('payment.cash') + value).length, 0)).fill(' ').join('')}${value}\n`)

        if (order.change_amount) {
          value = `${formatNumber(order.change_amount)} ${info.originalCurrency}`
          printer.addText(`${i18n.t('orders.change-amount')}:${Array(Math.max(MAX_LEN_SUMMARY - (i18n.t('orders.change-amount') + value).length, 0)).fill(' ').join('')}${value}\n`)
        }

        if (order.remaining_amount) {
          value = `${formatNumber(order.remaining_amount)} ${info.originalCurrency}`
          printer.addText(`${i18n.t('orders.remain-amount')}:${Array(Math.max(MAX_LEN_SUMMARY - (i18n.t('orders.remain-amount') + value).length, 0)).fill(' ').join('')}${value}\n`)
        }
      }

      printer.addText(DIVIDER)

      printer.addText(`${i18n.t('orders.ordered-at').toString()}: ${info.orderTime}\n${info.orderDate}\n`)
      if (printer?.addSymbol) {
        printer.addSymbol(info.webappUrl, printer.SYMBOL_QRCODE_MODEL_1, printer.LEVEL_M || printer.LEVEL_DEFAULT, 8, 0, 0)
      }
      printer.addText(DIVIDER)
      printer.addText(`${i18n.t('orders.thanks-see-soon')}\n`)
      printer.addCut(printer.CUT_FEED)
      printer.addPulse(printer.DRAWER_1, printer.PULSE_100)

      printer.send()

      return true
    } catch (e) {
      console.error(e)
      if (printer?.reset) {
        //        printer.reset()
      }
      return false
    }
  }
}

export default EpsonPrinter
